import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

export const COOKIE_CONSENT_KEY = 'performance-rh-cookie-consent';
export const COOKIE_CONSENT_VERSION = 1;

export interface CookieConsent {
  essenciais: true;
  analiticos: boolean;
  marketing: boolean;
  versao: number;
  data: string;
}

export interface CookiePreferencias {
  analiticos: boolean;
  marketing: boolean;
}

interface CookieConsentContextValue {
  consent: CookieConsent | null;
  /** Banner visível quando ainda não há escolha registrada. */
  bannerVisivel: boolean;
  /** Diálogo de preferências detalhadas aberto. */
  preferenciasAbertas: boolean;
  abrirPreferencias: () => void;
  fecharPreferencias: () => void;
  aceitarTodos: () => void;
  rejeitarNaoEssenciais: () => void;
  salvarPreferencias: (prefs: CookiePreferencias) => void;
  reabrirBanner: () => void;
  /** Categorias efetivamente ativas (sem consentimento => apenas essenciais). */
  analiticosAtivos: boolean;
  marketingAtivo: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent, removeConsent] = useLocalStorage<CookieConsent | null>(
    COOKIE_CONSENT_KEY,
    null
  );
  const [preferenciasAbertas, setPreferenciasAbertas] = useState(false);

  const registrar = useCallback(
    (prefs: CookiePreferencias) => {
      setConsent({
        essenciais: true,
        analiticos: prefs.analiticos,
        marketing: prefs.marketing,
        versao: COOKIE_CONSENT_VERSION,
        data: new Date().toISOString(),
      });
      setPreferenciasAbertas(false);
    },
    [setConsent]
  );

  const value = useMemo<CookieConsentContextValue>(() => {
    const valido = !!consent && consent.versao === COOKIE_CONSENT_VERSION;
    return {
      consent: valido ? consent : null,
      bannerVisivel: !valido,
      preferenciasAbertas,
      abrirPreferencias: () => setPreferenciasAbertas(true),
      fecharPreferencias: () => setPreferenciasAbertas(false),
      aceitarTodos: () => registrar({ analiticos: true, marketing: true }),
      rejeitarNaoEssenciais: () => registrar({ analiticos: false, marketing: false }),
      salvarPreferencias: registrar,
      reabrirBanner: () => {
        removeConsent();
        setPreferenciasAbertas(true);
      },
      analiticosAtivos: valido ? consent!.analiticos : false,
      marketingAtivo: valido ? consent!.marketing : false,
    };
  }, [consent, preferenciasAbertas, registrar, removeConsent]);

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent deve ser usado dentro de CookieConsentProvider');
  return ctx;
}
